// CMS OAP targets, transcribed from Payment Amounts Tables 3 (eCKM, p9) / 4 (CKM, p10-11); a measure
// is met if EITHER route holds (p5). CMS's numbers, not the care plan's guideline targets.

import type { FilingTrack } from '../baseline';
import type { DiagnosisClasses } from '../cmsRequiredMeasures';

export type OapTrack = FilingTrack;
export type OapMeasure = 'bp' | 'weight' | 'hba1c' | 'ldl_c' | 'egfr' | 'uacr';

type Dx = Pick<DiagnosisClasses, 'diabetes' | 'prediabetes' | 'ckd' | 'dyslipidemia' | 'ascvd'>;

/* OAP-TARGETS-START */
interface ScalarSpec {
  kind: 'scalar';
  /** Diagnosis classes that make the target apply; empty = every beneficiary. */
  gate: Array<keyof Dx>;
  controlLt: number;
  /** A stricter control threshold that replaces `controlLt` when the class is present. */
  controlLtIf?: { dx: keyof Dx; lt: number };
  /** Minimum-improvement delta from the filed baseline, in the measure's unit; null = no such route. */
  improvementDelta: number | null;
}
interface WeightSpec { kind: 'weight'; gate: []; controlBmiLt: number; controlMaxGainPct: number; improvementPct: number }
interface ReportOnlySpec { kind: 'report_only'; gate: Array<keyof Dx> }

export type TargetSpec = ScalarSpec | WeightSpec | ReportOnlySpec;

const BP: ScalarSpec = {
  kind: 'scalar', gate: [], controlLt: 130, improvementDelta: -15,
};
const WEIGHT: WeightSpec = {
  kind: 'weight', gate: [], controlBmiLt: 30, controlMaxGainPct: 5, improvementPct: -5,
};

export const OAP_TARGETS: Record<OapTrack, Partial<Record<OapMeasure, TargetSpec>>> = {
  eckm: {
    bp: BP,
    weight: WEIGHT,
    // Table 3: "Prediabetes only: Final HbA1c < 6.5%" and the improvement cell is a bare
    // "Not Applicable" — the one asymmetry between the two tables. Control is the ONLY route.
    hba1c: {
      kind: 'scalar', gate: ['prediabetes'], controlLt: 6.5, improvementDelta: null,
    },
    ldl_c: {
      kind: 'scalar', gate: ['dyslipidemia'], controlLt: 100, improvementDelta: -30,
    },
    // eGFR / uACR are absent from Table 3 altogether.
  },
  ckm: {
    bp: BP,
    weight: WEIGHT,
    // "1% point reduction" is one percentage POINT of HbA1c (7.8 → 6.8), not a relative 1 %.
    hba1c: {
      kind: 'scalar', gate: ['diabetes'], controlLt: 7.5, improvementDelta: -1,
    },
    ldl_c: {
      kind: 'scalar', gate: ['dyslipidemia', 'ascvd'], controlLt: 100, controlLtIf: { dx: 'ascvd', lt: 70 }, improvementDelta: -30,
    },
    // Table 4 footnote 8: no target "given disease trajectory variability"; success = submission.
    egfr: { kind: 'report_only', gate: ['diabetes', 'ckd'] },
    uacr: { kind: 'report_only', gate: ['diabetes', 'ckd'] },
  },
};
/* OAP-TARGETS-END */

export type GoalRoute = 'control' | 'improvement';

export interface ResolvedGoal {
  /** The route the filed baseline makes binding — the LESS demanding of the two (ties → control). */
  route: GoalRoute;
  /** Upper bound the current value must be strictly below (control) or at most (improvement). */
  target: number;
  controlLt: number;
  improvementTarget: number | null;
}

/** The spec that applies to this patient, or null when the measure carries no target for them. */
export function goalSpecFor(track: OapTrack, measure: OapMeasure, dx: Dx): TargetSpec | null {
  const spec = OAP_TARGETS[track][measure];
  if (!spec) return null;
  if (spec.gate.length > 0 && !spec.gate.some((k) => dx[k])) return null;
  return spec;
}

// The LESS demanding route binds (met if either); without a filed baseline, control alone.
export function resolveScalarGoal(spec: ScalarSpec, dx: Dx, baseline: number | null): ResolvedGoal {
  const controlLt = spec.controlLtIf && dx[spec.controlLtIf.dx] ? spec.controlLtIf.lt : spec.controlLt;
  const improvementTarget = spec.improvementDelta != null && baseline != null ? baseline + spec.improvementDelta : null;
  // Control is "< X"; improvement is "≤ baseline + delta". Lower is better on every scalar measure,
  // so the less demanding route is the one with the HIGHER bound.
  if (improvementTarget != null && improvementTarget > controlLt) {
    return {
      route: 'improvement', target: improvementTarget, controlLt, improvementTarget,
    };
  }
  return {
    route: 'control', target: controlLt, controlLt, improvementTarget,
  };
}

export interface WeightGoal {
  route: GoalRoute;
  /** Weight (kg) the patient must stay at or under to meet the binding route. */
  targetKg: number;
  /** Control route's own bound: BMI < 30 AND ≤ +5 % — the weight at which BOTH hold. */
  controlKg: number | null;
  improvementKg: number;
}

// Control = BMI < 30 AND ≤ +5 %: the weight where both hold is min(BMI-30 weight, baseline × 1.05),
// the BMI-30 weight being baseline × 30 / filedBmi (height² = weight / BMI).
export function resolveWeightGoal(spec: WeightSpec, baselineKg: number, baselineBmi: number | null): WeightGoal {
  const improvementKg = baselineKg * (1 + spec.improvementPct / 100);
  const gainCapKg = baselineKg * (1 + spec.controlMaxGainPct / 100);
  const bmiCapKg = baselineBmi != null && baselineBmi > 0 ? baselineKg * (spec.controlBmiLt / baselineBmi) : null;
  const controlKg = bmiCapKg == null ? null : Math.min(bmiCapKg, gainCapKg);
  if (controlKg != null && controlKg >= improvementKg) {
    return {
      route: 'control', targetKg: controlKg, controlKg, improvementKg,
    };
  }
  return {
    route: 'improvement', targetKg: improvementKg, controlKg, improvementKg,
  };
}
